import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { environment } from '../../environments/environment';

/**
 * Service to handle authentication tokens specifically for the backend API
 * This ensures we get tokens with the correct audience (backend Client ID)
 * instead of Microsoft Graph tokens
 */
@Injectable({
  providedIn: 'root'
})
export class BackendAuthService {

  baseURL = environment.backend_base_url;

  // Dedupe/serialize concurrent token requests so multiple simultaneous HTTP calls
  // don't each kick off their own acquireTokenSilent (which can stall MSAL internally).
  private pendingTokenPromise: Promise<string> | null = null;

  constructor(private msalService: MsalService, public _http: HttpClient) { }

  /**
   * Acquire a token specifically for the backend API
   * This token will have the backend's Client ID in the 'aud' claim
   * @returns Promise<string> The access token for the backend API
   */
  async getBackendToken(): Promise<string> {
    if (this.pendingTokenPromise) {
      return this.pendingTokenPromise;
    }

    this.pendingTokenPromise = this.acquireBackendToken().finally(() => {
      this.pendingTokenPromise = null;
    });

    return this.pendingTokenPromise;
  }

  private async acquireBackendToken(): Promise<string> {
    const accounts = this.msalService.instance.getAllAccounts();
    
    if (accounts.length === 0) {
      throw new Error('No active account found');
    }

    const backendTokenRequest = {
      scopes: [environment.msal.backendApiScope],
      account: accounts[0]
    };

    try {
      // Guard against acquireTokenSilent hanging (e.g. stuck hidden-iframe renewal) instead of resolving/rejecting
      const response = await this.withTimeout(
        this.msalService.instance.acquireTokenSilent(backendTokenRequest),
        15000,
        'Silent backend token acquisition timed out'
      );
      // console.log('Backend API Token acquired:', {
      //   scopes: response.scopes,
      //   expiresOn: response.expiresOn
      // });
      return response.accessToken;
    } catch (error) {
      console.error('Silent token acquisition failed, attempting interactive:', error);
      
      // Guard against the popup being blocked/never resolving, which would otherwise hang every backend request forever
      const response = await this.withTimeout(
        this.msalService.instance.acquireTokenPopup(backendTokenRequest),
        20000,
        'Interactive backend token acquisition timed out (popup may be blocked)'
      );
      return response.accessToken;
    }
  }

  private withTimeout<T>(promise: Promise<T>, ms: number, message: string): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error(message)), ms);
      promise.then(
        (value) => { clearTimeout(timer); resolve(value); },
        (err) => { clearTimeout(timer); reject(err); }
      );
    });
  }

  /**
   * Test backend authentication by acquiring a token and validating it with the backend
   * Useful for debugging and verifying the auth flow
   * @param backendUrl Optional backend URL (defaults to localhost:3000 for testing)
   */
  async testBackendAuth() {
    const token = await this.getBackendToken();
    
    const headers = new HttpHeaders({ 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` });
    const url = `${this.baseURL}/api/test-auth`;
    console.log('Call backend test-auth with token:', url, headers, { tokenSnippet: token});
    return this._http.get<any>(url, { headers }).pipe(
      map(response => {
        console.log("Get test-auth response:", response);
      })
    );
  }

  /**
   * Get a token for Microsoft Graph API (for user profile, calendar, etc.)
   * This is separate from the backend API token
   * @returns Promise<string> The access token for Microsoft Graph
   */
  async getGraphToken(): Promise<string> {
    const accounts = this.msalService.instance.getAllAccounts();
    
    if (accounts.length === 0) {
      throw new Error('No active account found');
    }

    const graphTokenRequest = {
      scopes: ['User.Read', 'Calendars.ReadWrite', 'Mail.Send'],
      account: accounts[0]
    };

    try {
      const response = await this.msalService.instance.acquireTokenSilent(graphTokenRequest);
      return response.accessToken;
    } catch (error) {
      console.error('Silent Graph token acquisition failed:', error);
      const response = await this.msalService.instance.acquireTokenPopup(graphTokenRequest);
      return response.accessToken;
    }
  }
}
